import { CommonModule, DatePipe } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { CaseBehaviour, CaseDetailModel } from '../../../../insights.interface';
import { finalize, Observable, Subject, take, takeUntil } from 'rxjs';
import { InsightMode } from '../../../../insights.enums';
import { DRAWER_IDS } from '../../../../../../core/constants/drawer-ids.constant';
import { DrawerService } from '../../../../../../core/services/drawer.service';
import { LoaderService } from '../../../../../../core/services/loader.service';
import { ReportFormComponent } from '../report-form/report-form.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import {
  ProgressReportGraphMapping,
  reportDrawerParams,
  ReportPrintData,
  ReportRes,
} from '../report-manager.interface';
import { ReportGraphComponent } from '../report-graph/report-graph.component';
import { ReportManagerService } from '../report-manager.service';
import { CommonService } from '../../../../../../shared/services/common.service';
import { ErrorResponse } from '../../../../../../core/models/error.models';
import { GraphList } from '../../../insights-data/insights-data.interface';
import { UserDatePipe } from '../../../../../../shared/pipes/user-date.pipe';
@Component({
  selector: 'app-create-report',
  standalone: true,
  imports: [
    SvgIconComponent,
    CommonModule,
    NzSpinModule,
    NzButtonModule,
    ReportFormComponent,
    ReportGraphComponent,
    UserDatePipe,
  ],
  providers: [DatePipe],
  templateUrl: './create-report.component.html',
  styleUrl: './create-report.component.scss',
})
export class CreateReportComponent implements OnDestroy {
  loading$: Observable<boolean>;
  private readonly destroy$ = new Subject<void>();
  caseParams: reportDrawerParams = {
    case: {} as CaseDetailModel,
    mode: InsightMode.PRINT_CASE,
    selectedReport: null,
  };
  stepNumber = 1;
  caseDetail: CaseDetailModel = {} as CaseDetailModel;
  reportForm = {
    formValue: {} as ReportRes,
    isFormValid: false,
  };
  reportData: ReportRes | null | undefined = null;
  graphData: ProgressReportGraphMapping[] = [];
  printData: ReportPrintData[] = [];
  constructor(
    private loaderService: LoaderService,
    private drawerService: DrawerService,
    private reportService: ReportManagerService,
    private commonService: CommonService,
    private datePipe: DatePipe
  ) {
    this.loading$ = this.loaderService.loading$;
    this.setupDrawerSubscription();
  }

  private setupDrawerSubscription(): void {
    this.drawerService
      .getParams<reportDrawerParams>(DRAWER_IDS.CREATE_REPORT)
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        if (params) {
          this.caseParams = params;
          this.caseDetail = params.case;
          this.reportData = params.selectedReport;
        }
      });
  }

  get isEditMode() {
    return this.caseParams.mode === InsightMode.EDIT_REPORT;
  }

  formValueChange(event: { formValue: ReportRes; isFormValid: boolean }): void {
    this.reportForm = event;
  }

  handleGraph(event: ProgressReportGraphMapping[]) {
    this.graphData = event;
  }

  onNext() {
    if (this.reportForm.isFormValid) {
      this.stepNumber++;
    }
  }
  onPrev() {
    this.stepNumber--;
    this.reportData = this.reportForm.formValue;
  }
  submitReport() {
    const formValue = this.reportForm.formValue;
    formValue.progressReportGraphMappings = this.graphData;
    formValue.caseId = this.caseDetail.id;
    const graphList = formValue.graphList;
    formValue.startDate = this.datePipe.transform(
      formValue.startDate,
      'yyyy-MM-dd'
    )!;
    formValue.endDate = this.datePipe.transform(
      formValue.endDate,
      'yyyy-MM-dd'
    )!;
    delete formValue.graphList;
    this.loaderService.show();
    const operation$ = this.reportService.createReport(formValue);
    operation$
      .pipe(
        take(1),
        takeUntil(this.destroy$),
        finalize(() => {
          this.loaderService.hide();
        })
      )
      .subscribe({
        next: () => {
          this.generatePrintData(
            graphList ?? [],
            formValue.progressReportGraphMappings,
            this.caseDetail.caseBehaviors
          );
          this.closeDrawer(true);
        },
        error: error => {
          this.loaderService.hide();
          this.handleError(error);
        },
      });
  }

  private handleError({ errors, message }: ErrorResponse): void {
    const errorMessages = errors.map(
      err => err.description || err.detailedMessage
    );
    this.commonService.notify('error', errorMessages, message);
  }

  generatePrintData(
    graphList: GraphList[],
    progressReportGraphMappings: ProgressReportGraphMapping[],
    caseBehaviors: CaseBehaviour[]
  ): boolean {
    const result = progressReportGraphMappings.map(mapping => {
      const matchingGraph = graphList.find(
        graph => graph.id === mapping.activityDataUnitId
      );
      const matchingBehavior = caseBehaviors.find(
        behavior => behavior.id === matchingGraph?.caseBehaviorId
      );

      return {
        category: matchingGraph?.behaviorCategoryName || 'Unknown',
        behavior:
          matchingGraph?.behaviorTypes.map(bt => bt.name).join(', ') ||
          'Unknown',
        operationalDefinition:
          matchingBehavior?.operationalDefinition || 'No Data',
        goals: matchingBehavior?.goals || 'No Goals',
        graphImage: matchingGraph?.graphImage || [],
        graphTotalImage: matchingGraph?.graphImageTotal || [],
        commentsData: matchingGraph?.commentsData || [],
      };
    });
    this.printData = result;
    return true;
  }

  closeDrawer(isSuccess = false) {
    const printData = {
      reportData: this.reportForm.formValue,
      printData: this.printData,
      isEditMode: this.isEditMode,
    };
    const drawerRef = this.drawerService.getDrawerRef(DRAWER_IDS.CREATE_REPORT);
    drawerRef?.close({ isSuccess, printData: isSuccess ? printData : null });
    if (this.isEditMode) {
      const drawerRef = this.drawerService.getDrawerRef(DRAWER_IDS.EDIT_REPORT);
      drawerRef?.close({ isSuccess, printData: isSuccess ? printData : null });
    }
    const reportManagerDrawerRef = this.drawerService.getDrawerRef(
      DRAWER_IDS.REPORT_MANAGER
    );
    if (isSuccess) {
      reportManagerDrawerRef?.close({
        isSuccess,
        printData: isSuccess ? printData : null,
      });
    }
  }

  onCancel() {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_REPORT);
  }
  ngOnDestroy(): void {
    this.loaderService.hide();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
